import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import React, { useEffect, useMemo, useState } from "react";
import { DialogBody, DialogFooter, DialogHeader, GenericDialog } from "../dialogs/GenericDialog";
import { X } from "@phosphor-icons/react";
import PrimaryButton from "../buttons/PrimaryButton";
import DiscountedProductCard from "components/coupons/discounted-product-selector/DiscountedProductCard";
import {
  DiscountedProductSelectorItemType,
  DiscountedProductSelectorPayloadType,
  DiscountedProductSelectorProductDistributionType,
  DiscountedProductSelectorProductType,
  DiscountedProductSelectorProductVariantType,
  ShippingHandlesType,
} from "lib/types/checkout";
import DiscountedProductGroup from "components/coupons/discounted-product-selector/DiscountedProductGroup";
import { postRequest } from "lib/core/apiClient";
import OverlaySpinner from "../loaders/OverlaySpinner";
import { errorToast } from "lib/utils/toasters";
import { mutate } from "swr";
import FreeGiftsIcon from "assests/images/free-gifts-logo.png";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { analyticsEvents, analyticsTypes, apiURI } from "lib/utils/constants";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { getUpsellUrl } from "lib/utils/upsell";
import { useUserContext } from "lib/contexts/UserProvider";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import { PopupPayloadMap, DiscountProductSelectorMode } from "lib/types/popup";

interface DiscountedProductSelectorPopupProps {
  position?: "cart" | "checkout";
}

const DiscountedProductSelectorPopup: React.FC<DiscountedProductSelectorPopupProps> = ({
  position = "checkout",
}) => {
  const { t } = useLocale();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    state: {
      checkoutId,

      discountedProductSelectorData,
      checkoutView,
      discountProductSelectorPayload,

      addresses,
      checkoutLoading,
    },
    actions: {
      setDiscountProductSelectorModal,
      setCheckoutView,
      setShippingHandles,
      updateCheckoutBasedOnCheckoutResponse,
      setDiscountProductSelectorPayload,
    },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: { user, isAddressLoading },
  } = useUserContext();
  const { openPopup, activePopup } = usePopupOrchestrator();
  const selectorPayload =
    activePopup?.key === "DISCOUNTED_PRODUCT_SELECTOR"
      ? (activePopup.payload as PopupPayloadMap["DISCOUNTED_PRODUCT_SELECTOR"])
      : null;
  const selectorMode = selectorPayload?.mode ?? null;
  const popupPosition = selectorPayload?.position ?? position;
  const [localMode, setLocalMode] = useState<DiscountProductSelectorMode | null>(null);
  const effectiveMode = localMode ?? selectorMode;
  const [openState, setOpenState] = useState<boolean[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();

  const { initialCheckoutStep } = checkoutMetadata ?? {};

  useEffect(() => {
    setLocalMode(null);
  }, [activePopup?.id]);

  const hasMultipleGroups = useMemo(
    () => discountedProductSelectorData.items.length > 1,
    [discountedProductSelectorData],
  );

  useEffect(() => {
    if (openState.length === 0) {
      setOpenState(
        discountedProductSelectorData.items.map(
          (value: DiscountedProductSelectorItemType, index: number) => index === 0,
        ),
      );
    }
  }, [discountedProductSelectorData]);

  useEffect(() => {
    if (effectiveMode === "DISCOUNTED_PRODUCT_SELECTOR") {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_ITEMS_SELECTOR_LOADED,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "load",
        metaData: {
          itemSelectorData: {
            discountCodes: discountCodes,
          },
        },
      });
    }
  }, [effectiveMode, activePopup?.id]);

  const updatedPayload: DiscountedProductSelectorPayloadType = useMemo(() => {
    const selectedVariants: Record<string, DiscountedProductSelectorProductVariantType[]> = {};
    discountedProductSelectorData.items.forEach((item: DiscountedProductSelectorItemType) => {
      if (!Boolean(selectedVariants[item.couponCode])) {
        selectedVariants[item.couponCode] = [];
      }
      item.products.forEach((product: DiscountedProductSelectorProductType) => {
        product.variants?.forEach((variant: DiscountedProductSelectorProductVariantType) => {
          if (Boolean(variant.selectedQuantity)) {
            if (selectedVariants[item.couponCode]) {
              selectedVariants[item.couponCode].push(variant);
            } else {
              selectedVariants[item.couponCode] = [variant];
            }
          }
        });
      });
    });

    const generatedPayload: DiscountedProductSelectorPayloadType = {
      selection: Object.entries(selectedVariants).map(([key, items]) => {
        return {
          discount_code: key,
          items: items?.map((variant: DiscountedProductSelectorProductVariantType) => ({
            variant_id: variant.id,
            quantity: variant.selectedQuantity,
            has_components: variant.hasComponents,
          })),
        };
      }),
    };
    return generatedPayload;
  }, [discountedProductSelectorData]);

  const discountCodes = useMemo(() => {
    const discountCodes: string[] = [];
    discountedProductSelectorData?.items?.forEach((item: DiscountedProductSelectorItemType) => {
      if (!discountCodes?.includes(item?.couponCode)) {
        discountCodes.push(item?.couponCode);
      }
    });
    return discountCodes;
  }, [discountedProductSelectorData]);

  const appliedPayload: DiscountedProductSelectorPayloadType = useMemo(() => {
    const modifiedSelection = discountCodes.map((code: string) => {
      const couponIndex = discountProductSelectorPayload.selection.findIndex(
        (item) => item.discount_code === code,
      );
      if (couponIndex !== -1) {
        return {
          discount_code: code,
          items: discountProductSelectorPayload.selection[couponIndex].items,
        };
      }
      return {
        discount_code: code,
        items: [],
      };
    });
    return { selection: modifiedSelection };
  }, [discountProductSelectorPayload, discountCodes, discountedProductSelectorData]);

  const onSubmit = async (payload: DiscountedProductSelectorPayloadType) => {
    setIsLoading(true);
    try {
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
      let response: any;
      const AUTH_DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount-items`);
      if (isAuthenticated) {
        response = await postRequest(AUTH_DISCOUNT_PATH, payload, "KRATOS_PRIVATE");
      } else {
        response = await postRequest(AUTH_DISCOUNT_PATH, payload, "KRATOS_PUBLIC");
      }
      if (!response) {
        errorToast(t("unable_to_add"));
        return;
      }
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_ITEMS_SELECTOR_SUBMITTED,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "click",
        metaData: {
          itemSelectorData: {
            selection: payload.selection,
          },
        },
      });
      setDiscountProductSelectorPayload(payload);
      // This response adds/updates the freebie line item, so its available_addons
      // is recomputed against the new item arrangement (same as an /items mutation) -
      // trusting it here is what makes an add-on eligible on the freebie item show up
      // immediately instead of only after a later /items call refreshes state.
      updateCheckoutBasedOnCheckoutResponse(response, "MAIN", true);
      mutate(getUpsellUrl({ checkoutId }));
      mutate(DISCOUNT_PATH);

      const isAddressServiceable = response?.pricing?.serviceable ?? false;
      if (!Boolean(isAddressServiceable) && popupPosition !== "cart") {
        if (Boolean(checkoutView === "PAYMENTS") && Boolean(initialCheckoutStep !== "PAYMENTS")) {
          setCheckoutView("ADDRESS_LIST");
          errorToast(t("serviceability_error"), 5000);
        }
        if (Boolean(checkoutView === "PAYMENTS") && Boolean(initialCheckoutStep === "PAYMENTS")) {
          openPopup("ADDRESS_LIST", {});
          errorToast(t("serviceability_error"), 5000);
        }
        return;
      }
      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      setShippingHandles(shippingHandles as ShippingHandlesType);
      if (
        shippingHandles.length > 0 &&
        Boolean(showShippingHandles) &&
        checkoutView === "PAYMENTS" &&
        initialCheckoutStep !== "PAYMENTS"
      ) {
        openPopup("SHIPPING_HANDLES", {});
      } else {
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`UPI_INTENT`);
      }
    } catch (error) {
      console.error(error);
      errorToast(((error as any)?.response?.data?.error?.message as string) ?? t("generic_api_error"));
    } finally {
      setIsLoading(false);
      setDiscountProductSelectorModal("NONE");
      //for new users attributes will be synced, discount-item api might fail, but address needs to be selected
      if (
        !addresses?.shipping_address_id &&
        !Boolean(user?.addresses?.length) &&
        !checkoutLoading &&
        !isAddressLoading
      ) {
        openPopup("ADDRESS_LIST", {});
      }
    }
  };

  const discountedProductSelectorProductDistribution: DiscountedProductSelectorProductDistributionType =
    useMemo(() => {
      let freebies = 0;
      let discountedProducts = 0;
      discountedProductSelectorData.items.forEach((item: DiscountedProductSelectorItemType) => {
        if (item.concessionAmount === 100) {
          freebies += item.quantity;
        } else {
          discountedProducts += item.quantity;
        }
      });
      return {
        freebies: freebies,
        discountedProducts: discountedProducts,
        onlyFreebies: freebies === discountedProductSelectorData.totalItems,
        onlyDiscountedProducts: discountedProducts === discountedProductSelectorData.totalItems,
        freebiesAndDiscountedProducts: freebies > 0 && discountedProducts > 0,
      };
    }, [discountedProductSelectorData]);

 
  return (
    <>
      <GenericDialog
        isOpen={effectiveMode === "DISCOUNTED_PRODUCT_SELECTOR"}
        setIsOpen={() => onSubmit(appliedPayload)}
        translateAxis="y"
        customClass={`overflow-scroll md:!top-auto md:absolute !scrollbar-hide h-full rounded-t-2xl min-h-[20rem] h-auto max-h-[40rem]`}
        dialogOverlay={true}
        closeOnOverlayClick>
        <DialogHeader className="!p-[18px] flex justify-between  !rounded-t-2xl !from-white !via-white !to-white border-b border-gray-light">
          <div className="flex flex-col items-start justify-between">
            <p className="text-base font-medium text-coal-dark">{t("select_items")}</p>
            <span className="text-sm font-normal text-coal-dark">{`${discountedProductSelectorData.totalSelectedItems}/${discountedProductSelectorData.totalItems}`}</span>
          </div>
          <div className="cursor-pointer flex text-coal-dark justify-start items-start">
            <X className="w-6 h-6" onClick={() => onSubmit(appliedPayload)} />
          </div>
        </DialogHeader>
        <DialogBody className="scrollbar-hide max-h-[36rem]">
          <div className="flex flex-col !scrollbar-hide items-start justify-between border-t border-gray-light pb-28 pt-2">
            {hasMultipleGroups ? (
              discountedProductSelectorData.items.map(
                (item: DiscountedProductSelectorItemType, index: number) => (
                  <DiscountedProductGroup
                    key={`discounted-product-group-${index + 1}`}
                    productGroup={item}
                    groupIndex={index}
                    openState={openState}
                    setOpenState={setOpenState}
                  />
                ),
              )
            ) : (
              <div className="flex flex-col w-full space-y-2 pt-2 pb-28 px-3">
                {discountedProductSelectorData.items?.[0]?.products?.map(
                  (product: DiscountedProductSelectorProductType, pIndex: number) => (
                    <DiscountedProductCard
                      key={`discounted-product-card-${pIndex + 1}`}
                      selectedProduct={product}
                      groupIndex={0}
                      productIndex={pIndex}
                    />
                  ),
                )}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <PrimaryButton
            buttonText={t("confirm")}
            onClick={() => onSubmit(updatedPayload)}
            height="h-14"
            isCheckout={true}
            id="flo_address__btn"
            isLoading={isLoading}
          />
        </DialogFooter>
      </GenericDialog>
      <GenericDialog
        isOpen={effectiveMode === "FREE_GIFTS"}
        translateAxis="y"
        customClass={`overflow-scroll md:!top-auto md:absolute !scrollbar-hide h-full max-h-[40vh]`}
        dialogOverlay={true}
        closeOnOverlayClick>
        <DialogBody className="!p-0">
          <div className="flex flex-col items-center justify-end pb-[3rem] h-full space-y-16">
            <div className="flex flex-col items-center justify-center space-y-4 w-full">
              <img src={FreeGiftsIcon} alt="free-gifts" className="w-10 h-10" />
              <div className="flex flex-col items-center justify-center space-y-1">
                <p className="text-base font-medium text-coal-dark text-center px-4">
                  {discountedProductSelectorProductDistribution.onlyFreebies && (
                    <>
                      {discountedProductSelectorProductDistribution.freebies > 1
                        ? t("get_x_freebies_on_purchase", {
                            x: discountedProductSelectorProductDistribution.freebies,
                          })
                        : t("get_a_freebie_on_purchase")}
                    </>
                  )}
                  {discountedProductSelectorProductDistribution.onlyDiscountedProducts && (
                    <>
                      {discountedProductSelectorProductDistribution.discountedProducts > 1
                        ? t("get_x_discounted_products_on_purchase", {
                            x: discountedProductSelectorProductDistribution.discountedProducts,
                          })
                        : t("get_a_discounted_product_on_purchase")}
                    </>
                  )}
                  {discountedProductSelectorProductDistribution.freebiesAndDiscountedProducts && (
                    <>
                      {discountedProductSelectorProductDistribution.freebies > 1 &&
                      discountedProductSelectorProductDistribution.discountedProducts > 1
                        ? t("get_x_freebies_and_y_discounted_products_on_purchase", {
                            x: discountedProductSelectorProductDistribution.freebies,
                            y: discountedProductSelectorProductDistribution.discountedProducts,
                          })
                        : discountedProductSelectorProductDistribution.freebies > 1 &&
                          discountedProductSelectorProductDistribution.discountedProducts === 1
                        ? t("get_x_freebies_and_a_discounted_product_on_purchase", {
                            x: discountedProductSelectorProductDistribution.freebies,
                          })
                        : discountedProductSelectorProductDistribution.freebies === 1 &&
                          discountedProductSelectorProductDistribution.discountedProducts > 1
                        ? t("get_a_freebie_and_x_discounted_products_on_purchase", {
                            x: discountedProductSelectorProductDistribution.discountedProducts,
                          })
                        : t("get_a_freebie_and_a_discounted_product_on_purchase")}
                    </>
                  )}
                </p>
                <span className="text-sm font-normal text-coal-dark">
                  {Boolean(discountedProductSelectorProductDistribution.freebies)
                    ? discountedProductSelectorProductDistribution.freebies > 1
                      ? t("free_gifts_unlocked")
                      : t("free_gift_unlocked")
                    : discountedProductSelectorProductDistribution.discountedProducts > 1
                    ? t("discounted_products_unlocked")
                    : t("discounted_product_unlocked")}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center space-y-4 w-full px-4">
              <button
                onClick={() => setLocalMode("DISCOUNTED_PRODUCT_SELECTOR")}
                className="text-sm font-medium text-btnPrimaryText bg-primary-dark px-8 py-3 rounded-lg w-full">
                {t("select_items")}
              </button>
              <span
                onClick={() => onSubmit(appliedPayload)}
                className="text-sm font-medium cursor-pointer text-carbon-dark">
                {t("skip")}
              </span>
            </div>
          </div>
        </DialogBody>
        {/* <DialogFooter>
          <div className="h-16 bg-gradient-to-b from-transparent via-[#fffffff2] to-[#fffffff2]" />
        </DialogFooter> */}
      </GenericDialog>
      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default DiscountedProductSelectorPopup;